import { ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductSubtypeEnum } from '@app/models/enums/product-subtype.enum';
import { ProductTypeEnum } from '@app/models/enums/product-type.enum';
import { SearchStatusEnum } from '@app/models/enums/search-status.enum';
import { ISearchFilters } from '@app/models/interfaces/ISearchFilters';
import { SearchParamsInterface } from '@app/models/interfaces/search-params.interface';
import { SearchResultsInterface } from '@app/models/interfaces/search-results.interface';
import { TfBiozWidgetService } from '@app/modules/bioz';
import { PriceService } from '@app/modules/tf-pricing';
import { GEProductInterface } from '@app/products/ge/assays/models/interfaces/ge-product.interface';
import { LayoutService } from '@app/services/layout.service';
import { RelatedArrayCountService } from '@app/services/related-array-count.service';
import { StoreService } from '@app/services/store.service';
import { TfMetaService } from '@app/services/tf-meta.service';
import { Subscription } from 'rxjs';
import { LocalStorageService } from '@app/services/local-storage-service';

@Component({
    selector: 'tf-assays-search-page',
    templateUrl: './search-page.component.html',
    styleUrls: ['./search-page.component.scss']
})
export class TfSearchPageComponent implements OnInit, OnDestroy {

    selectedTabName: "assay" | "array" = "assay";
    totalHits: number;
    relatedArraysCount: any = {};
    filterState: ISearchFilters[] = [];

    @ViewChild("compareViewContainer", {read: ViewContainerRef}) compareViewContainer: ViewContainerRef;

    searchResults: SearchResultsInterface = null;

    SearchStatusEnum = SearchStatusEnum;
    searchStatus: SearchStatusEnum = null;

    keyword = "*";

    productType: ProductTypeEnum = ProductTypeEnum.ge;
    productSubtype: ProductSubtypeEnum = ProductSubtypeEnum.ge;
    keywordUsed : string = "*";

    private subscriptions: Subscription = new Subscription();
    private comparePanelModuleLoaded: boolean = false;

    @HostBinding('class.selected') selected: boolean = false;

    showMultiplexOptions: boolean = false;
    enableNPAssaysFeature: boolean = false;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly layoutService: LayoutService,
        private readonly router: Router,
        private readonly storeService: StoreService,
        private readonly priceService: PriceService,
        private readonly cdr: ChangeDetectorRef,
        private readonly relatedArrayCountService: RelatedArrayCountService,
        private readonly biozWidgetService: TfBiozWidgetService,
        private readonly metaService: TfMetaService,
        private localStorageService: LocalStorageService
    ) {
    }

    ngOnInit(): void {

        this.metaService.setMeta(this.productSubtype);

        this.storeService.unselectAllProducts();

        this.subscribeToQueryParams();
        this.subscribeToSearchStatus();
        this.subscribeToSearchResults();
        this.subscribeToSelectedProducts();

        this.showMultiplexOptions = this.localStorageService.getItem("showGEMultiplexOptions") || false;
        this.enableNPAssaysFeature = this.localStorageService.isNPAssaysFeatureEnabled();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private subscribeToQueryParams(): void {

        const queryParamsSubscription = this.route.queryParams.subscribe((params) => {

            this.keyword = params['keyword'] ? params['keyword'] : "*";

            const searchParams: Partial<SearchParamsInterface> = {
                keyword:  this.keyword,
                productType: ProductTypeEnum.ge,
                productSubtype: ProductSubtypeEnum.ge
            };

            if(params['filters']) {
                searchParams.filters = params['filters'];
                searchParams.broadSearch = true;
            }

            this.storeService.resetSearch();
            this.storeService.setSearchParams(searchParams);
            this.storeService.doSearch();
        });

        this.subscriptions.add(queryParamsSubscription);

    }

    private subscribeToSearchStatus(): void {

        const searchStatusSubscription = this.storeService.searchStatus$.subscribe((status) => {

            if (![SearchStatusEnum.initial, SearchStatusEnum.searching].includes(status)) {
                this.layoutService.showFooter();
            }

            if(status === SearchStatusEnum.doneWithNoResults){
                this.navigateToGlobalSearch(this.searchResults.keyword, this.productType);
            }

            this.searchStatus = status;

        });
        this.subscriptions.add(searchStatusSubscription);

    }

    private subscribeToSearchResults(): void {

        const searchResultsSubscription = this.storeService.searchResults$.subscribe((searchResults) => {

            if (searchResults === null) {
                return;
            }

            this.metaService.setMeta(this.productSubtype, searchResults.totalHits);

            this.searchResults = searchResults;
            this.productType = this.storeService.searchParams$.getValue().productType;
            this.keyword = this.searchResults.keyword;
            this.keywordUsed = this.searchResults.keywordUsed;

            if (this.searchResults && this.searchResults.totalHits === 0 && this.searchResults.products[this.getProductTypeEnum()].length === 0 && this.searchStatus == SearchStatusEnum.doneWithNoResultsFilters) {
                this.navigateToGlobalSearch(this.keywordUsed, this.productType);
            }

            this.fetchPricingData();
            this.getRelatedArraysCount();
            this.loadBiozInfo();

        });

        this.subscriptions.add(searchResultsSubscription);

    }

    private subscribeToSelectedProducts(): void {

        const subscription = this.storeService.selectedResults$.subscribe(() => {
            if (this.storeService.getSelectedProductsCount() > 0 && this.comparePanelModuleLoaded === false) {
                void this.loadComparePanelModule();
            }
        });

        this.subscriptions.add(subscription);

    }

    getProducts(): GEProductInterface[] {
        return this.searchResults.products[this.getProductTypeEnum()];
    }

    navigateToGlobalSearch(keyword: string, productType:string): void{
        void this.router.navigate(['results'], {
            queryParams: {
                keyword: keyword,
                productType: productType
            },
            replaceUrl: true
        });
    }

    getFilterState() {

        const filterStateSubscription = this.storeService.tempFilters$.subscribe((filter) => {
            this.filterState = filter.filterState;
        });

        this.subscriptions.add(filterStateSubscription);

    }

    getRelatedArraysCount(): void {
        if (this.searchResults.products[this.getProductTypeEnum()].length > 0) {
            const productIds = this.searchResults.products[this.getProductTypeEnum()].map((row) => row.productID).join(',');
            this.relatedArrayCountService.getRelatedArraysCount('GX', 'GX', productIds).subscribe({
                next: response => {
                    this.relatedArraysCount = response;
                },
                error: error => {
                    console.error({message: "Related Array Count API error", error: error})
                },
                complete: () => this.cdr.detectChanges()
            })
        }
    }

    showComparePanel(): boolean {
        return this.storeService.getSelectedProductsCount() > 0
    }

    fetchPricingData(): void {

        const skus: string[] = [];

        this.searchResults.products[this.getProductTypeEnum()].forEach((product: GEProductInterface) => {
            skus.push(product.defaultCatalogNum);
        });

        this.priceService.preloadPricingInfo(skus);

    }

    loadBiozInfo(): void {
        const productIds = this.searchResults.products[this.getProductTypeEnum()].map((product) => product.productID);
        this.biozWidgetService.preloadCitations(productIds);
    }

    async loadComparePanelModule(): Promise<void> {

        const {ComparePanelComponent} = await import("@app/modules/compare");

        const component = this.compareViewContainer.createComponent(ComparePanelComponent);

        component.instance.productType = "ge";

        this.comparePanelModuleLoaded = true;

    }

    getProductTypeEnum(): ProductTypeEnum {
        return ProductTypeEnum[this.productType];
    }

    onToggleChange($event:CustomEvent) {
        this.localStorageService.setItem('showGEMultiplexOptions', $event.detail);
    }
}
